import { Checkbox, Dialog, FormControlLabel, Popover, Tooltip } from "@mui/material";
import BulkEditBlog from "./BulkEditBlog";
import {
  ArrowLeft,
  Edit,
  Filter,
  PlusCircle,
  Trash,
  Trash2
} from "feather-icons-react/build/IconComponents";
import { t } from "i18next";
import { useContext, useEffect, useMemo, useCallback, useRef, useState } from "react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { fetchBlogs, updateBlog, deleteBlogs } from "../../services/blogService";
import { DataLoad, PlanPermission, TableColumnsContext } from "../../App";
import { NOPHOTO_IMAGE } from "../../Assets/StaticData/StaticImage";
import Breadcrumbs from "../../controllers/breadCrumbs";
import { formatDate } from "../../controllers/FormatAmount";
import { clientData } from "../../controllers/GetClient";
import MuiSwitch from "../../controllers/MuiSwitch";
import { parentNavigate } from "../../controllers/ParentNavigate";
import { useUser } from "../../controllers/UserContext";
import { useAuthor } from "../../controllers/AuthorContext";
import { useRoutingByType } from "../../controllers/useRouting";
import { isAscendingOrder } from "../FormInput/CommonTable";
import {
  ButtonComponent,
  CommonTable,
  ConfirmationPopup,
  PageLimitDropDownComponent,
  PaginationComponent,
  SearchComponent,
  TableStatusDropdown
} from "@shopeasy-dashboard-ui/ui-library";
import NoRecords from "../Main/NoRecords";
import Notification from "../Notification";
import * as defaultCustomFields from "../../utilities/defaultCustomTableField";
import useTableApi from "../../utilities/useTableApi";

const getInitialUrlParams = (location) => {
  const urlParams = new URLSearchParams(window.location.search);
  const typeParam = urlParams.get("type") || "all";
  const searchParam =
    urlParams.get("s") ||
    urlParams.get("search") ||
    (location?.state?.searchkey ? location.state.searchkey : "");
  const pageParam = parseInt(urlParams.get("page"), 10);
  return { typeParam, searchParam, pageParam };
};

function MyBlogs() {
  const handleTableApi = useTableApi();
  const { fetchAuthors } = useAuthor();
  const queryClient = useQueryClient();
  const location = useLocation();

  useEffect(() => {
    fetchAuthors();
  }, [fetchAuthors]);

  const initialUrlParams = useMemo(() => getInitialUrlParams(location), []);

  const [Page, setPage] = useState(() => {
    if (initialUrlParams.pageParam && initialUrlParams.pageParam > 0) {
      return initialUrlParams.pageParam;
    }
    if (location?.state?.prevPage) return Number(location.state.prevPage);
    return 1;
  });

  const { dataLoading, setDataLoading } = useContext(DataLoad);
  const [clearRow, setClearRow] = useState(false);
  const initialLimit = localStorage.getItem("limit");
  const [limit, setLimit] = useState(initialLimit || 10);
  const [search, setSearch] = useState(initialUrlParams.searchParam);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [blogData, setBlogData] = useState();
  const { sortData, isUserLoaded } = useUser();
  const userContext = useUser();
  const savedSort = sortData?.blogs || sortData?.blog;

  const [sortBy, setSortBy] = useState(
    () => (location?.state?.accending ? location?.state?.field : savedSort?.sort_by || "createdAt")
  );
  const [orderBy, setOrderBy] = useState(() => {
    if (location?.state?.accending) {
      return { orderByValue: "ASC", isDecending: false };
    }
    const isAsc = isAscendingOrder(savedSort?.order_by);
    return {
      orderByValue: savedSort?.order_by ? (isAsc ? "ASC" : "DESC") : "DESC",
      isDecending: savedSort?.order_by ? !isAsc : true,
    };
  });
  const isInitialSortLoaded = useRef(Boolean(location?.state?.field || savedSort?.sort_by));
  const [isNotification, setIsNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState("");
  const [severity, setSeverity] = useState();
  const [allBlog, setAllBlog] = useState();
  const [publishBlog, setPublishBlog] = useState();
  const [draftBlog, setDraftBlog] = useState();
  const [openTab, setOpenTab] = useState(initialUrlParams.typeParam);
  const { setPlanPermission } = useContext(PlanPermission);
  const [openBulkEditModal, setOpenBulkEditModal] = useState(false);
  const [selectedRowsId, setSelectedRowsId] = useState([]);
  const [confirmationPopup, setConfirmationPopup] = useState(false);
  const [filter, setFilter] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const { tableColumnsData } = useContext(TableColumnsContext);

  const apiFieldNames = useMemo(() => {
    const dataObj = tableColumnsData?.data?.data || tableColumnsData?.data || tableColumnsData;
    if (Array.isArray(dataObj?.blogs)) return dataObj.blogs;
    if (Array.isArray(tableColumnsData?.blogs)) return tableColumnsData.blogs;
    return [];
  }, [tableColumnsData]);

  const dynamicColumns = useMemo(() => {
    const fixedKeys = new Set([
      "_id", "__v", "client", "tenant", "store_client", "store_tenant", "user",
      "additional_images", "images", "image", "title", "blogcategory",
      "active", "createdAt", "updatedAt", "comment_count", "action", "actions",
      "checkbox", "selection", "select", "drag_handle", "draghandle", "drag",
      "blog_id", "import_id", "blog_slug_name", "sort_order", "is_draft",
    ]);

    return apiFieldNames
      .filter((fieldName) => typeof fieldName === "string" && fieldName.trim() && !fixedKeys.has(fieldName))
      .map((fieldName) => {
        const isDotted = fieldName.includes(".");
        const parts = isDotted ? fieldName.split(".") : null;

        const label = fieldName
          .replace(/\./g, " › ")
          .replace(/[-_]/g, " ")
          .replace(/([a-z])([A-Z])/g, "$1 $2")
          .replace(/\b\w/g, (c) => c.toUpperCase());

        const getValue = (row) => {
          if (!row) return undefined;
          if (isDotted && parts) {
            return parts.reduce((obj, k) => (obj != null ? obj[k] : undefined), row);
          }
          return row[fieldName];
        };

        return {
          key: fieldName,
          title: label,
          width: "160px",
          minWidth: "110px",
          responsiveWidth: "100px",
          headerClassName: "px-3 whitespace-nowrap truncate",
          cellClassName: "px-3 truncate",
          render: (row) => {
            const val = getValue(row);
            if (val === null || val === undefined || val === "") return "-";
            if (typeof val === "boolean") return val ? "Yes" : "No";
            if (fieldName.toLowerCase().includes("date") && typeof val === "string") {
              return formatDate(val);
            }
            if (typeof val === "object") return JSON.stringify(val);
            return String(val);
          },
        };
      });
  }, [apiFieldNames]);

  const blogRoute = useRoutingByType("blog");
  const protocol = window.location.protocol + "//";

  useEffect(() => {
    if (location?.state?.searchkey) {
      setSearch(location.state.searchkey);
    }
  }, [location?.state?.searchkey]);

  // Debounced search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  // Synchronize state when browser navigation / popstate / URL parameters change
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search || window.location.search);
    const typeParam = urlParams.get("type") || "all";
    const searchParam = urlParams.get("s") || urlParams.get("search") || "";
    const pageParam = parseInt(urlParams.get("page"), 10) || 1;

    if (typeParam !== openTab) {
      setOpenTab(typeParam);
    }
    if (searchParam !== search && !location?.state?.searchkey) {
      setSearch(searchParam);
    }
    if (pageParam !== Page) {
      setPage(pageParam);
    }
  }, [location.search]);

  // Maintain URL and browser history automatically synchronized with openTab, search, and Page
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    if (openTab && openTab !== "all") {
      urlParams.set("type", openTab);
    } else {
      urlParams.delete("type");
    }

    if (search && search.trim()) {
      urlParams.set("search", search.trim());
    } else {
      urlParams.delete("search");
      urlParams.delete("s");
    }

    if (Page > 1) {
      urlParams.set("page", Page);
    } else {
      urlParams.delete("page");
    }

    const newQueryString = urlParams.toString();
    const newUrl = newQueryString
      ? `${window.location.pathname}?${newQueryString}`
      : window.location.pathname;

    if (window.location.search !== (newQueryString ? `?${newQueryString}` : "")) {
      window.history.replaceState({}, "", newUrl);
      parentNavigate(newUrl);
    }
  }, [openTab, search, Page]);

  // Reset page and selection when tab changes
  const isFirstMountTab = useRef(true);
  useEffect(() => {
    if (isFirstMountTab.current) {
      isFirstMountTab.current = false;
      return;
    }
    setSelectedRowsId([]);
    setPage(1);
  }, [openTab]);

  useEffect(() => {
    if (!isInitialSortLoaded.current && isUserLoaded) {
      isInitialSortLoaded.current = true;
      if (savedSort && savedSort.sort_by) {
        const isAsc = isAscendingOrder(savedSort.order_by);
        const targetOrderBy = isAsc ? "ASC" : "DESC";
        if (savedSort.sort_by !== sortBy || targetOrderBy !== orderBy.orderByValue) {
          setSortBy(savedSort.sort_by);
          setOrderBy({
            orderByValue: targetOrderBy,
            isDecending: !isAsc,
          });
        }
      }
    }
  }, [savedSort, isUserLoaded, sortBy, orderBy.orderByValue]);

  // Sorting callback
  const SortData = useCallback((sort_by, explicitOrder) => {
    setSortBy(sort_by);
    let nextOrder = "DESC";
    if (explicitOrder !== undefined) {
      nextOrder = isAscendingOrder(explicitOrder) ? "ASC" : "DESC";
    } else if (sortBy === sort_by) {
      const isCurrentlyAsc = isAscendingOrder(orderBy);
      nextOrder = isCurrentlyAsc ? "DESC" : "ASC";
    } else {
      nextOrder = "DESC";
    }
    setOrderBy({
      orderByValue: nextOrder,
      isDecending: nextOrder === "DESC",
    });
  }, [sortBy, orderBy]);

  // TanStack Query for Blog Table with 60s cache and smooth placeholder pagination
  const blogQueryKey = useMemo(() => [
    "blogs",
    openTab,
    Page,
    limit,
    (debouncedSearch || "").trim(),
    sortBy,
    orderBy.orderByValue,
  ], [openTab, Page, limit, debouncedSearch, sortBy, orderBy.orderByValue]);

  const {
    data: blogQueryResult,
    isFetching: isBlogFetching,
    error: blogQueryError,
  } = useQuery({
    queryKey: blogQueryKey,
    queryFn: () =>
      fetchBlogs({
        page: Page,
        limit,
        searchkey: debouncedSearch,
        sortBy,
        orderBy: orderBy.orderByValue,
        openTab,
      }),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
    enabled: isUserLoaded || isInitialSortLoaded.current || Boolean(location?.state?.field),
  });


  useEffect(() => {
    if (blogQueryResult) {
      const result = blogQueryResult;
      const totalRecords = result?.data?.totalRecords || 0;
      const formattedRecords = totalRecords.toString().padStart(2, "0");
      setBlogData(result);

      if (openTab === "all") {
        setAllBlog(formattedRecords);
        if (result?.data?.publishCount !== undefined) {
          setPublishBlog(result.data.publishCount.toString().padStart(2, "0"));
        }
        if (result?.data?.draftCount !== undefined) {
          setDraftBlog(result.data.draftCount.toString().padStart(2, "0"));
        }
      } else if (openTab === "publish") {
        setPublishBlog(formattedRecords);
        if (result?.data?.allCount !== undefined) {
          setAllBlog(result.data.allCount.toString().padStart(2, "0"));
        }
        if (result?.data?.draftCount !== undefined) {
          setDraftBlog(result.data.draftCount.toString().padStart(2, "0"));
        }
      } else if (openTab === "draft") {
        setDraftBlog(formattedRecords);
        if (result?.data?.allCount !== undefined) {
          setAllBlog(result.data.allCount.toString().padStart(2, "0"));
        }
        if (result?.data?.publishCount !== undefined) {
          setPublishBlog(result.data.publishCount.toString().padStart(2, "0"));
        }
      }
    }
  }, [blogQueryResult, openTab]);

  useEffect(() => {
    setDataLoading(Boolean(isBlogFetching));
  }, [isBlogFetching, setDataLoading]);

  useEffect(() => {
    return () => {
      setDataLoading(false);
    };
  }, [setDataLoading]);

  useEffect(() => {
    if (blogQueryError) {
      if (blogQueryError?.response?.status === 882) {
        setPlanPermission({ isPlanPermissions: false, forModule: "blog" });
      }
      setIsNotification(true);
      setNotificationMsg(blogQueryError?.message || blogQueryError);
      setSeverity("error");
    }
  }, [blogQueryError, setPlanPermission]);

  const handlePublishBlog = useCallback(async (id, value) => {
    // 1. Optimistic update of local blogData state
    setBlogData((prev) => {
      if (!prev?.data?.records) return prev;
      return {
        ...prev,
        data: {
          ...prev.data,
          records: prev.data.records.map((item) =>
            item._id === id ? { ...item, published: value } : item
          ),
        },
      };
    });

    // 2. Optimistic update across all cached queries in queryClient
    queryClient.setQueriesData({ queryKey: ["blogs"] }, (oldData) => {
      if (!oldData) return oldData;
      if (oldData?.data?.records) {
        return {
          ...oldData,
          data: {
            ...oldData.data,
            records: oldData.data.records.map((item) =>
              item._id === id ? { ...item, published: value } : item
            ),
          },
        };
      }
      if (oldData?.records) {
        return {
          ...oldData,
          records: oldData.records.map((item) =>
            item._id === id ? { ...item, published: value } : item
          ),
        };
      }
      return oldData;
    });

    // 3. Show instant notification
    setIsNotification(true);
    setNotificationMsg(value === true ? "Blog is set to active" : "Blog is set to hidden");
    setSeverity("success");

    // 4. Background API call (WITHOUT invalidating queries/refetching table)
    try {
      const payload = {
        published: value,
      };
      await updateBlog(id, payload);
    } catch (error) {
      console.error(error, "error----blog");
      // Rollback on error
      setBlogData((prev) => {
        if (!prev?.data?.records) return prev;
        return {
          ...prev,
          data: {
            ...prev.data,
            records: prev.data.records.map((item) =>
              item._id === id ? { ...item, published: !value } : item
            ),
          },
        };
      });

      queryClient.setQueriesData({ queryKey: ["blogs"] }, (oldData) => {
        if (!oldData) return oldData;
        if (oldData?.data?.records) {
          return {
            ...oldData,
            data: {
              ...oldData.data,
              records: oldData.data.records.map((item) =>
                item._id === id ? { ...item, published: !value } : item
              ),
            },
          };
        }
        if (oldData?.records) {
          return {
            ...oldData,
            records: oldData.records.map((item) =>
              item._id === id ? { ...item, published: !value } : item
            ),
          };
        }
        return oldData;
      });

      setIsNotification(true);
      setNotificationMsg(error?.message || error || "Failed to update blog status");
      setSeverity("error");
    }
  }, [queryClient]);


  const handleBulkEdit = useCallback((e) => {
    e.preventDefault();
    if (selectedRowsId.length > 50) {
      setIsNotification(true);
      setNotificationMsg("Maximum 50 blogs can be bulk edited at a time.");
      setSeverity("error");
      return;
    }
    setOpenBulkEditModal(true);
  }, [selectedRowsId.length]);

  const handleAddNewBlog = useCallback(() => {
    const prevUrl = window.location.pathname + window.location.search;
    parentNavigate("/blog/post/add", { prevPage: Page, prevUrl });
  }, [Page]);

  const EditBlog = useCallback((elem) => {
    const prevUrl = window.location.pathname + window.location.search;
    parentNavigate(`/blog/post/${elem?.blog_slug_name}`, { row: elem, prevPage: Page, prevUrl });
  }, [Page]);

  const HandleRemove = useCallback(async (id) => {
    const targetIds =
      selectedRowsId && selectedRowsId.length > 0 ? [...selectedRowsId] : id ? [id] : [];
    if (targetIds.length === 0) {
      setConfirmationPopup(false);
      return;
    }

    const previousBlogData = blogData || blogQueryResult;
    const previousAllCount = allBlog;
    const previousPublishCount = publishBlog;
    const previousDraftCount = draftBlog;

    const targetRecords = (previousBlogData?.data?.records || []).filter((x) =>
      targetIds.includes(x?._id)
    );

    // 1. Capture snapshot of all cached queries under 'blogs' for rollback
    const previousQueries = queryClient.getQueriesData({ queryKey: ["blogs"] });

    const currentSearch = (debouncedSearch || "").trim();
    const currentSortBy = sortBy;
    const currentOrderBy = orderBy.orderByValue;
    const numLimit = limit === "all" ? Infinity : Number(limit) || 10;
    const deletedCount = targetIds.length;

    // 2. Find all cached queries for the current filter/sort criteria
    const matchingQueries = previousQueries.filter(([key]) => {
      if (!Array.isArray(key) || key[0] !== "blogs") return false;
      const [, qTab, , qLimit, qSearch, qSortBy, qOrderBy] = key;
      return (
        qTab === openTab &&
        String(qLimit) === String(limit) &&
        (qSearch || "").trim() === currentSearch &&
        (qSortBy || "") === (currentSortBy || "") &&
        (qOrderBy || "") === (currentOrderBy || "")
      );
    });

    // Sort matching queries by page number ascending
    matchingQueries.sort((a, b) => (Number(a[0][2]) || 1) - (Number(b[0][2]) || 1));

    // Determine current total records and pages from cache
    const currentData = queryClient.getQueryData(blogQueryKey);
    const oldTotal =
      currentData?.data?.totalRecords ?? currentData?.totalRecords ?? blogData?.data?.totalRecords ?? 0;
    const newTotal = Math.max(0, Number(oldTotal) - deletedCount);
    const newEffectiveLimit = limit === "all" ? (newTotal || 1) : (Number(limit) || 10);
    const newTotalPages = Math.ceil(newTotal / newEffectiveLimit) || 1;

    // Update totalRecords and totalPages across ALL cached blogs queries once
    queryClient.setQueriesData({ queryKey: ["blogs"] }, (oldData) => {
      if (!oldData) return oldData;
      if (oldData?.data) {
        return {
          ...oldData,
          data: {
            ...oldData.data,
            totalRecords: newTotal,
            totalPages: newTotalPages,
          },
        };
      }
      return {
        ...oldData,
        totalRecords: newTotal,
        totalPages: newTotalPages,
      };
    });

    if (Number.isFinite(numLimit) && numLimit > 0) {
      // Gather all cached page records and filter out targetIds
      const pageMap = new Map();
      matchingQueries.forEach(([key, qData]) => {
        const pageNum = Number(key[2]) || 1;
        const recs = [...(qData?.data?.records || qData?.records || [])].filter(
          (item) => !targetIds.includes(item?._id)
        );
        pageMap.set(pageNum, { key, qData, recs });
      });

      // Cascade shift items from page+1 into page, page+2 into page+1, etc.
      const sortedPageNums = Array.from(pageMap.keys()).sort((a, b) => a - b);
      const startIndex = sortedPageNums.indexOf(Page);

      if (startIndex !== -1) {
        for (let i = startIndex; i < sortedPageNums.length; i++) {
          const currP = sortedPageNums[i];
          const currEntry = pageMap.get(currP);

          // Fill current page up to numLimit by taking from subsequent cached pages
          for (let j = i + 1; j < sortedPageNums.length; j++) {
            const nextP = sortedPageNums[j];
            const nextEntry = pageMap.get(nextP);
            const needed = numLimit - currEntry.recs.length;
            if (needed <= 0) break;

            const toShift = nextEntry.recs.splice(0, needed);
            currEntry.recs.push(...toShift);
          }
        }
      }

      // Update queryClient with reorganized pages
      pageMap.forEach(({ key, qData, recs }) => {
        const updated = {
          ...qData,
          ...(qData?.data
            ? {
              data: {
                ...qData.data,
                records: recs,
                totalRecords: newTotal,
                totalPages: newTotalPages,
              },
            }
            : {
              records: recs,
              totalRecords: newTotal,
              totalPages: newTotalPages,
            }),
        };
        queryClient.setQueryData(key, updated);
        if (Number(key[2]) === Page) {
          setBlogData(updated);
        }
      });
    } else {
      // Limit is 'all' or non-numeric: remove deleted items
      queryClient.setQueriesData({ queryKey: ["blogs"] }, (oldData) => {
        if (!oldData) return oldData;
        const oldRecs = oldData?.data?.records || oldData?.records;
        if (!oldRecs) return oldData;
        const newRecs = oldRecs.filter((item) => !targetIds.includes(item?._id));
        if (oldData?.data) {
          return {
            ...oldData,
            data: {
              ...oldData.data,
              records: newRecs,
              totalRecords: newTotal,
              totalPages: newTotalPages,
            },
          };
        }
        return {
          ...oldData,
          records: newRecs,
          totalRecords: newTotal,
          totalPages: newTotalPages,
        };
      });
      const updatedCur = queryClient.getQueryData(blogQueryKey);
      if (updatedCur) setBlogData(updatedCur);
    }


    // Optimistically update tab counts
    setAllBlog((prev) => {
      const current = Number(prev) || 0;
      return Math.max(0, current - deletedCount).toString().padStart(2, "0");
    });

    let deletedPublishCount = 0;
    let deletedDraftCount = 0;

    targetRecords.forEach((item) => {
      if (item?.is_draft || openTab === "draft") {
        deletedDraftCount++;
      } else {
        deletedPublishCount++;
      }
    });

    if (deletedPublishCount > 0) {
      setPublishBlog((prev) =>
        Math.max(0, (Number(prev) || 0) - deletedPublishCount).toString().padStart(2, "0")
      );
    }
    if (deletedDraftCount > 0) {
      setDraftBlog((prev) =>
        Math.max(0, (Number(prev) || 0) - deletedDraftCount).toString().padStart(2, "0")
      );
    }

    setSelectedRowsId([]);
    setClearRow((prev) => !prev);
    setConfirmationPopup(false);
    setIsNotification(true);
    setNotificationMsg("Removed Successfully");
    setSeverity("success");

    // Check if current page now has 0 records and Page > 1, or Page > newTotalPages
    const updatedCurrentPageData = queryClient.getQueryData(blogQueryKey);
    const updatedRecords =
      updatedCurrentPageData?.data?.records || updatedCurrentPageData?.records || [];
    if (updatedRecords.length === 0 && Page > 1) {
      setPage((p) => Math.max(1, p - 1));
    } else if (Page > newTotalPages && newTotalPages >= 1) {
      setPage(newTotalPages);
    }


    try {
      await deleteBlogs(targetIds);
    } catch (error) {
      // Rollback on failure
      setBlogData(previousBlogData);
      previousQueries.forEach(([key, oldData]) => {
        queryClient.setQueryData(key, oldData);
      });
      setAllBlog(previousAllCount);
      setPublishBlog(previousPublishCount);
      setDraftBlog(previousDraftCount);
      setIsNotification(true);
      setNotificationMsg(error?.message || error || "Failed to remove blog(s)");
      setSeverity("error");
    }
  }, [
    selectedRowsId,
    blogData,
    blogQueryResult,
    allBlog,
    publishBlog,
    draftBlog,
    queryClient,
    debouncedSearch,
    sortBy,
    orderBy.orderByValue,
    limit,
    openTab,
    Page,
    blogQueryKey,
  ]);



  const handleClose = useCallback(() => {
    setIsNotification(false);
    setNotificationMsg("");
    setSeverity("");
  }, []);

  const handleCloseConfirm = useCallback((value) => {
    setConfirmationPopup(value);
  }, []);

  useEffect(() => {
    localStorage.setItem("limit", limit);
  }, [limit]);

  const handleSelectRow = useCallback((e, id) => {
    const records = blogData?.data?.records || [];
    if (e?.nativeEvent?.shiftKey) {
      setSelectedRowsId((prevSelected) => {
        const prevIndex = records.findIndex(
          (x) => x._id === prevSelected[prevSelected.length - 1]
        );
        const currIndex = records.findIndex((x) => x._id === id);
        const startIndex = Math.min(prevIndex, currIndex);
        const endIndex = Math.max(prevIndex, currIndex);
        const final = records.slice(startIndex, endIndex + 1);

        if (prevSelected.includes(id)) {
          return [
            ...new Set(prevSelected.filter((x) => !final.find((item) => item._id === x))),
          ];
        } else {
          return [...new Set([...prevSelected, ...final.map((item) => item._id)])];
        }
      });
    } else if (id === "all") {
      setSelectedRowsId((prevSelected) => {
        if (prevSelected?.length > 0) {
          return [];
        } else {
          return records.map((x) => x?._id);
        }
      });
    } else {
      setSelectedRowsId((prevSelected) => {
        const existId = prevSelected.includes(id);
        if (!existId) {
          return [...prevSelected, id];
        } else {
          return prevSelected.filter((x) => x !== id);
        }
      });
    }
  }, [blogData]);

  const resetFilter = useCallback((e) => {
    e.preventDefault();
    setFilter(false);
    setAnchorEl(null);
    setSearch(location?.state?.searchkey ? location?.state?.searchkey : "");
    setPage(1);
    setLimit(limit === "all" ? 10 : limit);
  }, [location?.state?.searchkey, limit]);

  const blogTotalRecords = blogData?.data?.totalRecords || 0;
  const blogEffectiveLimit = limit === "all" ? blogTotalRecords || 1 : Number(limit) || 10;
  const blogTotalPages = useMemo(() => {
    return Math.ceil(blogTotalRecords / blogEffectiveLimit) || 1;
  }, [blogTotalRecords, blogEffectiveLimit]);

  const limitValue = useMemo(() => {
    return limit === "all" ? blogData?.data?.totalRecords || 0 : Number(limit || 10);
  }, [limit, blogData?.data?.totalRecords]);

  const startRec = useMemo(() => {
    return blogData?.data?.currentPage ? (blogData.data.currentPage - 1) * limitValue + 1 : 0;
  }, [blogData?.data?.currentPage, limitValue]);

  const endRec = useMemo(() => {
    return blogData?.data?.currentPage
      ? Math.min(blogData.data.currentPage * limitValue, blogData.data.totalRecords || 0)
      : 0;
  }, [blogData?.data?.currentPage, limitValue, blogData?.data?.totalRecords]);


  const columns = useMemo(() => {
    const records = blogData?.data?.records || [];
    const isAllSelected = records.length > 0 && selectedRowsId.length === records.length;
    const isIndeterminate = selectedRowsId.length > 0 && selectedRowsId.length < records.length;

    return [
      {
        key: "checkbox",
        title: (
          <Checkbox
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            onChange={(e) => handleSelectRow(e, "all")}
          />
        ),
        align: "text-center",
        ignoreRowClick: true,
        width: "50px",
        minWidth: "50px",
        maxWidth: "50px",
        headerClassName: "!px-0 w-[50px] min-w-[50px] max-w-[50px] text-center",
        cellClassName: "!px-0 w-[50px] min-w-[50px] max-w-[50px] text-center",
        render: (row) => (
          <Checkbox
            checked={selectedRowsId.includes(row._id)}
            onChange={(e) => handleSelectRow(e, row._id)}
          />
        ),
      },
      {
        key: "image",
        title: t("lang_Image") || "Image",
        width: "75px",
        minWidth: "70px",
        maxWidth: "85px",
        responsiveWidth: "60px",
        headerClassName: "thead-image px-2 text-center",
        cellClassName: "table-image px-2 text-center",
        render: (row) => (
          <img
            width={40}
            height={50}
            onError={(e) => {
              e.target.src = NOPHOTO_IMAGE;
            }}
            src={row.image || NOPHOTO_IMAGE}
            className="p-0.5 max-h-full rounded-md mx-auto block"
            alt={row?.title}
          />
        ),
      },
      {
        key: "title",
        title: t("lang_Blog_Title") || "Blog Title",
        width: "500px",
        maxWidth: "550px",
        headerClassName: "px-3",
        cellClassName: "px-3",
        render: (row) => (
          <p className="line-clamp-2 text-xs text-[var(--primary-text-color)] font-medium">
            {row.title || "-"}
          </p>
        ),
      },
      {
        key: "blogcategory",
        title: t("lang_Blog_Category_Name") || "Blog Category Name",
        width: "500px",
        maxWidth: "550px",
        headerClassName: "px-3 overflow-hidden truncate",
        cellClassName: "px-3  overflow-hidden truncate",
        render: (row) => (
          <div className="flex flex-wrap items-center gap-1 py-1 overflow-hidden max-w-full">
            {row?.blogcategory?.map((item, idx) => (
              <span
                key={idx}
                className="bg-[var(--focus-tab-color)] border border-[var(--border-color)] inline-flex items-center whitespace-nowrap text-[12px] px-2.5 py-0.5 rounded-[6px] font-medium text-[var(--primary-text-color)]"
              >
                {item?.blogcategory_name}
              </span>
            ))}
          </div>
        ),
      },
      {
        key: "active",
        title: openTab === "publish" ? t("lang_active") || "Active" : "",
        width: "120px",
        minWidth: "100px",
        headerClassName: "px-3 text-right whitespace-nowrap",
        cellClassName: "px-3 text-right whitespace-nowrap",
        align: "text-right",
        ignoreRowClick: true,
        render: (row) => (
          <>
            {openTab === "publish" && (
              row?.blog_publish_date && new Date(row?.blog_publish_date) >= new Date() ? (
                <div>
                  <span className="block table-text text-right mb-0.5 font-medium">
                    {t("lang_active")}
                  </span>
                  <p className="flex table-text text-xs items-center justify-end">
                    {formatDate(row?.blog_publish_date)}
                  </p>
                </div>
              ) : (
                <label className="relative flex justify-end w-full">
                  <FormControlLabel
                    checked={row.published === true}
                    control={<MuiSwitch />}
                    onChange={() => handlePublishBlog(row?._id, !row?.published)}
                  />
                </label>
              )
            )}
          </>
        ),
      },
      {
        key: "createdAt",
        title: t("lang_created_date") || "Created Date",
        width: "150px",
        minWidth: "140px",
        headerClassName: "px-3 whitespace-nowrap",
        cellClassName: "px-3 whitespace-nowrap",
        render: (row) => (
          <span className="table-text text-xs whitespace-nowrap">
            {row?.createdAt ? formatDate(row.createdAt) : "-"}
          </span>
        ),
      },
      {
        key: "comment_count",
        title: t("lang_Comment_Count") || "Comment Count",
        width: "140px",
        maxWidth: "150px",
        headerClassName: "px-3  whitespace-nowrap",
        cellClassName: "px-3  whitespace-nowrap",
        render: (row) => (
          <span className="text-xs font-semibold text-[var(--primary-text-color)]">
            {row?.comment_count ?? 0}
          </span>
        ),
      },
      ...dynamicColumns,
      {
        key: "action",
        title: t("lang_action"),
        width: "100px",
        maxWidth: "110px",
        responsiveWidth: "110px",
        headerClassName: "px-3 text-right",
        cellClassName: "px-3 text-right",
        align: "text-right",
        ignoreRowClick: true,
        render: (row) => (
          <div className="flex items-center space-x-2.5 justify-end w-full">
            <div className="text-xs font-thin hover:text-gray-900">
              <Tooltip title="Preview" arrow>
                <a
                  href={
                    row?.blog_url ||
                    `${protocol}${clientData?.custom_domain || clientData?.customer_domain}${blogRoute}/${row?.blog_slug_name}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="feather feather-external-link cursor-pointer text-green-600 transition"
                  >
                    <g>
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                      <polyline points="15 3 21 3 21 9"></polyline>
                      <line x1="10" y1="14" x2="21" y2="3"></line>
                    </g>
                  </svg>
                </a>
              </Tooltip>
            </div>
            <Tooltip title="Edit" arrow>
              <button onClick={() => EditBlog(row)}>
                <Edit className="w-[16px] h-[16px] text-[var(--table-text-color)] hover:text-[var(--primary-text-color)] transition-colors" />
              </button>
            </Tooltip>
            <Tooltip title="Delete" arrow>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  setConfirmationPopup(true);
                  setSelectedRowsId([row?._id]);
                }}
              >
                <Trash className="w-[16px] h-[16px] text-red-500 hover:text-red-600 transition-colors" />
              </button>
            </Tooltip>
          </div>
        ),
      },
    ];
  }, [
    blogData?.data?.records,
    selectedRowsId,
    handleSelectRow,
    openTab,
    handlePublishBlog,
    dynamicColumns,
    protocol,
    clientData?.custom_domain,
    clientData?.customer_domain,
    blogRoute,
    EditBlog,
  ]);

  const tableHeaderFilter = useCallback((manageFieldsBtn) => {
    const totalRecords = blogData?.data?.totalRecords || 0;
    const shouldShow =
      dataLoading ||
      totalRecords > 0 ||
      Number(allBlog) > 0 ||
      Boolean(search);

    if (!shouldShow) return null;

    return (
      <div className="relative w-full border-b main-border-color secondary-bg-color px-3 py-2 flex flex-col pe-3 min-h-[50px]">
        <div className="flex flex-wrap items-center justify-between w-full gap-2 min-h-[38px]">
          <div className="flex items-center">
            <TableStatusDropdown
              value={openTab}
              onChange={(val) => {
                setOpenTab(val);
                setPage(1);
              }}
              options={[
                { label: t("lang_all"), value: "all", count: allBlog },
                ...(Number(publishBlog) > 0 ? [{ label: t("lang_published"), value: "publish", count: publishBlog }] : []),
                ...(Number(draftBlog) > 0 ? [{ label: t("lang_draft"), value: "draft", count: draftBlog }] : []),
              ]}
              placeholder={t("lang_all")}
            />
          </div>

          <div className="hidden md:flex items-center gap-2 ml-auto">
            <div className="w-[180px] md:w-[200px]">
              <SearchComponent
                placeholder="Search Blogs"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                onClear={() => {
                  setSearch("");
                  setPage(1);
                }}
                isClearable={true}
                size="sm"
              />
            </div>

            <div className="w-[80px] md:w-[100px] shrink-0 select-sm-height select-hide-label select-no-padding-y">
              <PageLimitDropDownComponent
                limit={limit}
                setLimit={setLimit}
                setPage={setPage}
                totalRecords={blogData?.data?.totalRecords || 0}
              />
            </div>
            {manageFieldsBtn}
          </div>

          <Tooltip title={t("lang_filter") || "Filter"} arrow>
            <button
              type="button"
              onClick={(e) => {
                setFilter(true);
                setAnchorEl(e.currentTarget);
              }}
              className="block md:hidden relative flex items-center justify-center p-2 border main-border-color rounded-lg bg-[var(--secondary-bg-color)] hover:bg-gray-50/50 transition-colors w-8 h-8 shrink-0"
            >
              <Filter className="h-4 w-4 text-[var(--table-text-color)]" />
            </button>
          </Tooltip>
        </div>

        {totalRecords > 0 && selectedRowsId?.length > 0 && (
          <div className="flex flex-wrap items-center gap-1 mt-1 w-full">
            <span className="text-sm px-2 whitespace-nowrap">
              {selectedRowsId?.length} {t("lang_selected")}
            </span>
            <span className="me-1">|</span>
            <ButtonComponent
              variant="outline"
              size="sm"
              className="mx-1"
              onClick={handleBulkEdit}
              icon={<Edit className="w-3.5 h-3.5" />}
            >
              {t("lang_bulk_edit", "Bulk Edit")}
            </ButtonComponent>
            <ButtonComponent
              variant="danger_outline"
              size="sm"
              icon={<Trash2 className="w-3.5 h-3.5" />}
              onClick={(e) => {
                e.preventDefault();
                setConfirmationPopup(true);
              }}
            >
              {t("lang_delete_selection")}
            </ButtonComponent>
          </div>
        )}
      </div>
    );
  }, [
    dataLoading,
    blogData?.data?.totalRecords,
    allBlog,
    publishBlog,
    draftBlog,
    search,
    openTab,
    limit,
    selectedRowsId,
    handleBulkEdit,
  ]);

  return (
    <>
      <ConfirmationPopup
        heading="Delete Blog Post"
        subheading="Do you really want to delete this blog post from your store?"
        confirmationPopup={confirmationPopup}
        handleCloseConfirm={handleCloseConfirm}
        runFunction={HandleRemove}
        maxWidth="600px"
      />

      {isNotification && notificationMsg && (
        <Notification message={notificationMsg} close={handleClose} severity={severity} />
      )}
      <div className="h-auto overflow-y-auto overflow-x-hidden pb-[64px] p-5">
        <div className="flex items-end justify-between page-header-pb">
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                parentNavigate("/dashboard");
              }}
              className="back-arrow -ml-3.5"
            >
              <ArrowLeft />
            </button>
            <div>
              <Breadcrumbs
                crumbs={[
                  { name: t("lang_dashboard"), path: `/dashboard` },
                  { name: t("lang_Blog_Post"), path: "/blog/post" },
                ]}
              />
              <h4 className="text-xl md:text-2xl font-semibold text-primary">
                {t("lang_Blog_Post")}
              </h4>
            </div>
          </div>
          <div className="flex items-center">
            <ButtonComponent
              variant="primary"
              icon={<PlusCircle className="w-4 h-4" />}
              onClick={handleAddNewBlog}
            >
              {t("lang_Add_New_Blog_Post")}
            </ButtonComponent>
          </div>

        </div>
        <div className="flex justify-between w-full space-x-4">
          <div className="w-full space-y-3">
            <div className="flex w-full h-full overflow-hidden relative min-w-0">
              <div className="w-full">
                <div className="w-full transition-all duration-100 mb-[10px]">
                  <CommonTable
                    tableId="blogs"
                    columns={columns}
                    data={blogData?.data?.records}
                    tableHeader={tableHeaderFilter}
                    onSort={SortData}
                    sortBy={sortBy}
                    orderBy={orderBy}
                    tableFooter={
                      blogData?.data && blogTotalRecords > 0 ? (
                        <PaginationComponent
                          count={blogTotalPages}
                          page={Page}
                          onChange={(e, v) => setPage(v)}
                          totalRecords={blogTotalRecords}
                          startRecord={startRec}
                          endRecord={endRec}
                          limit={limit}
                          recordsLabel="Records"
                        />
                      ) : null
                    }

                    loading={dataLoading}
                    emptyMessage={<NoRecords />}
                    rowClick={(row) => EditBlog(row)}
                    rowClassName={(row) =>
                      `select-none ${selectedRowsId?.find((x) => x === row?._id) ? "selected-row" : ""}`
                    }
                    NEWAPI={handleTableApi}
                    tableDefaults={defaultCustomFields?.defaultBlogField}
                    tableWiseAllowedSortKeys={defaultCustomFields?.BLOG_SORT_KEYS}
                    userState={userContext}
                  />
                </div>
              </div>
              <Popover
                open={filter}
                anchorEl={anchorEl}
                onClose={() => {
                  setFilter(false);
                  setAnchorEl(null);
                }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                PaperProps={{
                  style: { marginTop: "8px", minWidth: "300px", borderRadius: "8px" },
                }}
                className="rounded-[6px]"
              >
                <div className="p-[20px] filter secondary-bg-color">
                  <div>
                    <div className="flex justify-between items-center">
                      <p className="flex font-semibold text-sm m-0">
                        <Filter className="me-1 w-4 h-4" />
                        {t("lang_filter")}
                      </p>
                      <button
                        type="button"
                        onClick={(e) => resetFilter(e)}
                        className="text-white bg-gray-800 hover:bg-gray-900 focus:outline-none font-medium rounded-md text-sm px-5 py-2 me-1 mb-2 dark:bg-gray-800 dark:hover:bg-gray-700 dark:focus:ring-gray-700 dark:border-gray-700"
                      >
                        Reset Filter
                      </button>
                    </div>
                  </div>
                  <hr className="main-border-color my-3"></hr>
                  <div className="grid gap-y-5 table-text">
                    <div>
                      <span className="text-sm capitalize font-medium mb-1 inline-block">
                        {t("lang_Search")}
                      </span>
                      <SearchComponent
                        placeholder="Search Blogs"
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                          setPage(1);
                        }}
                        onClear={() => {
                          setSearch("");
                          setPage(1);
                        }}
                        isClearable={true}
                      />
                    </div>
                    <div>
                      <span className="text-sm table-text font-medium mb-1 inline-block">
                        {t("lang_set_items_limit")}
                      </span>
                      <PageLimitDropDownComponent
                        limit={limit}
                        setLimit={setLimit}
                        setPage={setPage}
                        totalRecords={blogData?.data?.totalRecords || 0}
                      />
                    </div>
                  </div>
                </div>
              </Popover>
            </div>
          </div>
        </div>
      </div>

      {openBulkEditModal && (
        <Dialog
          open={openBulkEditModal}
          disableEscapeKeyDown
          onClose={() => setOpenBulkEditModal(false)}
          fullScreen
          sx={{
            zIndex: 999999,
            "& .MuiDialog-paper": {
              zIndex: 999999,
              margin: 0,
              width: "100vw",
              height: "100vh",
              maxWidth: "100vw",
              maxHeight: "100vh",
              borderRadius: 0,
              backgroundColor: "transparent",
            },
          }}
        >
          <BulkEditBlog
            isBulkEdit={true}
            bulkBlogIds={selectedRowsId}
            onClose={() => {
              setOpenBulkEditModal(false);
              setSelectedRowsId([]);
            }}
            onSuccess={(updatedCount) => {
              setOpenBulkEditModal(false);
              setSelectedRowsId([]);
              setIsNotification(true);
              const count = typeof updatedCount === "number" ? updatedCount : 1;
              setNotificationMsg(
                `${count} blog${count > 1 ? "s" : ""} updated successfully!`
              );
              setSeverity("success");
              queryClient.invalidateQueries({ queryKey: ["blogs"] });
            }}
          />
        </Dialog>
      )}
    </>
  );
}

export default MyBlogs;
